import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { apiRequest, isApiError } from "@profitlens/api-client";
import type { MeOut } from "@profitlens/api-client";

import { bootstrap, isAuthed, setAccessToken } from "@/lib/session";

/**
 * 套在受保护路由外层：未登录跳 /login；正在拉 me 显示空白，避免一闪而过。
 * 管理后台额外校验：当前用户必须是 platform_admin，否则强制登出回登录页
 * （刷新 cookie 按域名共享，可能混入门户站普通租户用户的会话）。
 */
export function LoginGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    let active = true;
    bootstrap()
      .catch((err) => {
        // 401 是预期（没 cookie / cookie 过期），其余按会话失效处理
        if (isApiError(err) && err.isUnauthorized) setAccessToken(null);
      })
      .finally(() => {
        if (active) setBootstrapped(true);
      });
    return () => {
      active = false;
    };
  }, []);

  // 拉一次 /auth/me 验证 token 真有效（refresh 成功后 bootstrap 不会自动校准 header）
  const me = useQuery<MeOut | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await apiRequest<MeOut>("/auth/me");
      } catch (err) {
        if (isApiError(err) && err.isUnauthorized) setAccessToken(null);
        return null;
      }
    },
    enabled: bootstrapped && isAuthed(),
    staleTime: 60_000,
  });

  // 角色校验：拿到 me 后确认是平台管理员；不是就清会话（含刷新 cookie）
  useEffect(() => {
    if (!me.data) return;
    if (me.data.user?.role === "platform_admin") return;
    void apiRequest("/auth/logout", { method: "POST", body: { refresh_token: null } }).catch(
      () => undefined,
    );
    setAccessToken(null);
  }, [me.data]);

  if (!bootstrapped) {
    return (
      <div className="grid min-h-dvh place-items-center text-sm text-muted">
        {t("common.loading")}
      </div>
    );
  }
  if (!isAuthed() && location.pathname !== "/login") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}
